import { useState } from 'react';
import NavBar from '../components/NavBar';
import { getDesignTokens } from '../utils/designTokens';
import { useThemeStore } from '../store/themeStore';
import { Icon } from '../utils/icons';
import MemberAvatar from '../components/MemberAvatar';
import { EventMember } from '../types/calendar';
import { Todo, TodoStatus, TodoPriority } from '../types/todo';

// Mock data for demonstration
const mockMembers: EventMember[] = [
  { memberId: '1', memberName: 'Emma', memberColor: '#3b82f6' },
  { memberId: '2', memberName: 'Lucas', memberColor: '#ef4444' },
  { memberId: '3', memberName: 'Mom', memberColor: '#10b981' },
  { memberId: '4', memberName: 'Dad', memberColor: '#8b5cf6' },
];

const sampleTodos: Todo[] = [
  {
    todoId: '1',
    familyId: 'family-1',
    title: 'Buy groceries for dinner',
    description: 'Milk, eggs, bread, chicken',
    status: 'open',
    priority: 'high',
    dueDateTime: new Date(),
    assigneeId: '1',
    assigneeName: 'Emma',
    isFamily: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    todoId: '2',
    familyId: 'family-1',
    title: 'Call dentist to schedule appointment',
    status: 'open',
    priority: 'medium',
    dueDateTime: new Date(Date.now() + 86400000),
    assigneeId: '3',
    assigneeName: 'Mom',
    isFamily: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    todoId: '3',
    familyId: 'family-1',
    title: 'Finish homework assignment',
    description: 'Math chapter 5, problems 1-20',
    status: 'open',
    priority: 'critical',
    dueDateTime: new Date(Date.now() - 86400000), // Past due
    assigneeId: '2',
    assigneeName: 'Lucas',
    isFamily: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    todoId: '4',
    familyId: 'family-1',
    title: 'Plan weekend family activity',
    status: 'open',
    priority: 'low',
    assigneeId: undefined,
    assigneeName: undefined,
    isFamily: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    todoId: '5',
    familyId: 'family-1',
    title: 'Pick up dry cleaning',
    status: 'done',
    priority: 'medium',
    dueDateTime: new Date(Date.now() - 172800000),
    assigneeId: '4',
    assigneeName: 'Dad',
    isFamily: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
];

// Proposal 1: Enhanced Swipe Actions (Current + Delete)
function Proposal1Card({ todo, isDarkMode }: { todo: Todo; isDarkMode: boolean }) {
  const tokens = getDesignTokens(isDarkMode);
  const [swipeOffset] = useState(0);

  return (
    <div className="relative mb-3 overflow-hidden rounded-landing">
      {/* Swipe actions background */}
      <div className="absolute inset-0 flex">
        <div className="flex-1 bg-green-500 flex items-center justify-start pl-4">
          <Icon name="check" size="lg" className="text-white" />
          <span className="ml-2 text-white font-semibold">Complete</span>
        </div>
        <div className="w-20 bg-blue-500 flex items-center justify-center">
          <Icon name="edit" size="lg" className="text-white" />
        </div>
        <div className="w-20 bg-red-500 flex items-center justify-center">
          <Icon name="delete" size="lg" className="text-white" />
        </div>
      </div>

      {/* Todo card */}
      <div
        className={`${tokens.card.bg} ${tokens.border.light} border rounded-landing p-4 min-h-[80px] relative z-10 transition-transform touch-manipulation`}
        style={{
          transform: `translateX(${swipeOffset}px)`,
          borderLeftWidth: '4px',
          borderLeftColor: todo.status === 'done' ? '#9ca3af' : (todo.priority === 'critical' ? '#ef4444' : '#3b82f6'),
        }}
      >
        <div className="flex items-start gap-3">
          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
            todo.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300'
          }`}>
            {todo.status === 'done' && <Icon name="check" size="sm" className="text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-base font-semibold mb-1 ${
              todo.status === 'done' ? 'line-through text-gray-500' : tokens.text.primary
            }`}>
              {todo.title}
            </h3>
            {todo.description && (
              <p className={`text-sm mb-2 ${tokens.text.muted}`}>{todo.description}</p>
            )}
            <div className="flex items-center gap-3 flex-wrap">
              {todo.dueDateTime && (
                <div className="flex items-center gap-1">
                  <Icon name="clock" size="sm" className={tokens.text.muted} />
                  <span className={`text-xs ${tokens.text.muted}`}>Today</span>
                </div>
              )}
              {todo.assigneeName && (
                <MemberAvatar
                  member={mockMembers.find(m => m.memberName === todo.assigneeName) || mockMembers[0]}
                  size="sm"
                />
              )}
              {todo.priority && todo.priority !== 'low' && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  todo.priority === 'critical' ? 'bg-red-100 text-red-700' :
                  todo.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>
                  {todo.priority}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Proposal 2: Compact List View with Inline Actions
function Proposal2Card({ todo, isDarkMode }: { todo: Todo; isDarkMode: boolean }) {
  const tokens = getDesignTokens(isDarkMode);
  
  return (
    <div className={`${tokens.card.bg} ${tokens.border.light} border-l-4 rounded-landing p-3 mb-2 min-h-[64px] flex items-center gap-3 touch-manipulation`}
      style={{
        borderLeftColor: todo.status === 'done' ? '#9ca3af' : (todo.priority === 'critical' ? '#ef4444' : '#3b82f6'),
        opacity: todo.status === 'done' ? 0.7 : 1,
      }}
    >
      <button className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
        todo.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300'
      }`}>
        {todo.status === 'done' && <Icon name="check" size="sm" className="text-white" />}
      </button>
      <div className="flex-1 min-w-0">
        <h3 className={`text-sm font-semibold ${
          todo.status === 'done' ? 'line-through text-gray-500' : tokens.text.primary
        }`}>
          {todo.title}
        </h3>
        <div className="flex items-center gap-2 mt-1">
          {todo.dueDateTime && (
            <span className={`text-xs ${tokens.text.muted}`}>Today</span>
          )}
          {todo.assigneeName && (
            <MemberAvatar
              member={mockMembers.find(m => m.memberName === todo.assigneeName) || mockMembers[0]}
              size="sm"
            />
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button className={`p-2 rounded-landing ${tokens.button.secondary} min-w-[44px] min-h-[44px] flex items-center justify-center`}>
          <Icon name="edit" size="sm" />
        </button>
        <button className={`p-2 rounded-landing bg-red-100 text-red-700 min-w-[44px] min-h-[44px] flex items-center justify-center`}>
          <Icon name="delete" size="sm" />
        </button>
      </div>
    </div>
  );
}

// Proposal 3: Grouped by Priority/Date
function Proposal3View({ todos, isDarkMode }: { todos: Todo[]; isDarkMode: boolean }) {
  const tokens = getDesignTokens(isDarkMode);
  const grouped = {
    critical: todos.filter(t => t.priority === 'critical' && t.status === 'open'),
    today: todos.filter(t => t.dueDateTime && t.status === 'open'),
    upcoming: todos.filter(t => t.dueDateTime && t.status === 'open'),
    other: todos.filter(t => t.status === 'open' && !t.dueDateTime && t.priority !== 'critical'),
    done: todos.filter(t => t.status === 'done'),
  };

  return (
    <div className="space-y-4">
      {grouped.critical.length > 0 && (
        <div>
          <h3 className={`text-sm font-bold ${tokens.text.primary} mb-2 flex items-center gap-2`}>
            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
            Critical
          </h3>
          {grouped.critical.map(todo => (
            <Proposal2Card key={todo.todoId} todo={todo} isDarkMode={isDarkMode} />
          ))}
        </div>
      )}
      {grouped.today.length > 0 && (
        <div>
          <h3 className={`text-sm font-bold ${tokens.text.primary} mb-2 flex items-center gap-2`}>
            <Icon name="clock" size="sm" />
            Due Today
          </h3>
          {grouped.today.map(todo => (
            <Proposal2Card key={todo.todoId} todo={todo} isDarkMode={isDarkMode} />
          ))}
        </div>
      )}
      {grouped.other.length > 0 && (
        <div>
          <h3 className={`text-sm font-bold ${tokens.text.primary} mb-2`}>Other</h3>
          {grouped.other.map(todo => (
            <Proposal2Card key={todo.todoId} todo={todo} isDarkMode={isDarkMode} />
          ))}
        </div>
      )}
    </div>
  );
}

// Proposal 4: Quick Actions Bar
function Proposal4Card({ todo, isDarkMode }: { todo: Todo; isDarkMode: boolean }) {
  const tokens = getDesignTokens(isDarkMode);
  
  return (
    <div className={`${tokens.card.bg} ${tokens.border.light} border rounded-landing p-4 mb-3 min-h-[100px] touch-manipulation`}
      style={{
        borderLeftWidth: '4px',
        borderLeftColor: todo.status === 'done' ? '#9ca3af' : (todo.priority === 'critical' ? '#ef4444' : '#3b82f6'),
      }}
    >
      <div className="flex items-start gap-3 mb-3">
        <button className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
          todo.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300'
        }`}>
          {todo.status === 'done' && <Icon name="check" size="sm" className="text-white" />}
        </button>
        <div className="flex-1 min-w-0">
          <h3 className={`text-base font-semibold mb-1 ${
            todo.status === 'done' ? 'line-through text-gray-500' : tokens.text.primary
          }`}>
            {todo.title}
          </h3>
          {todo.description && (
            <p className={`text-sm ${tokens.text.muted}`}>{todo.description}</p>
          )}
        </div>
      </div>
      {/* Quick actions bar */}
      <div className="flex items-center gap-2 pt-3 border-t" style={{ borderColor: tokens.border.light }}>
        <button className={`flex-1 px-3 py-2 rounded-landing ${tokens.button.secondary} min-h-[44px] flex items-center justify-center gap-2`}>
          <Icon name="edit" size="sm" />
          <span className="text-sm">Edit</span>
        </button>
        <button className={`flex-1 px-3 py-2 rounded-landing ${todo.status === 'done' ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-700'} min-h-[44px] flex items-center justify-center gap-2`}>
          <Icon name={todo.status === 'done' ? 'arrow-left' : 'check'} size="sm" />
          <span className="text-sm">{todo.status === 'done' ? 'Reopen' : 'Complete'}</span>
        </button>
        <button className={`px-3 py-2 rounded-landing bg-red-100 text-red-700 min-h-[44px] min-w-[44px] flex items-center justify-center`}>
          <Icon name="delete" size="sm" />
        </button>
      </div>
    </div>
  );
}

// Proposal 5: Minimalist with Long Press Menu
function Proposal5Card({ todo, isDarkMode }: { todo: Todo; isDarkMode: boolean }) {
  const tokens = getDesignTokens(isDarkMode);
  
  return (
    <div className={`${tokens.card.bg} ${tokens.border.light} border rounded-landing p-4 mb-3 min-h-[72px] touch-manipulation`}
      style={{
        borderLeftWidth: '3px',
        borderLeftColor: todo.status === 'done' ? '#9ca3af' : (todo.priority === 'critical' ? '#ef4444' : '#3b82f6'),
      }}
    >
      <div className="flex items-center gap-3">
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
          todo.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300'
        }`}>
          {todo.status === 'done' && <Icon name="check" size="sm" className="text-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-semibold ${
            todo.status === 'done' ? 'line-through text-gray-500' : tokens.text.primary
          }`}>
            {todo.title}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            {todo.dueDateTime && (
              <span className={`text-xs ${tokens.text.muted}`}>Today</span>
            )}
            {todo.assigneeName && (
              <MemberAvatar
                member={mockMembers.find(m => m.memberName === todo.assigneeName) || mockMembers[0]}
                size="sm"
              />
            )}
          </div>
        </div>
        <div className="text-xs text-gray-400">Long press</div>
      </div>
    </div>
  );
}

// Full UI Layout Components
function FullUILayout1({ 
  todos, 
  isDarkMode, 
  scope, 
  statusFilter, 
  selectedListName,
  onScopeChange,
  onStatusChange,
  onListClick,
  onCreateList,
  onCreateTodo,
}: {
  todos: Todo[];
  isDarkMode: boolean;
  scope: 'my' | 'family';
  statusFilter: TodoStatus | 'all';
  selectedListName: string;
  onScopeChange: (scope: 'my' | 'family') => void;
  onStatusChange: (filter: TodoStatus | 'all') => void;
  onListClick: () => void;
  onCreateList: () => void;
  onCreateTodo: () => void;
}) {
  const tokens = getDesignTokens(isDarkMode);
  const filteredTodos = todos.filter(t => {
    if (statusFilter === 'all') return true;
    return t.status === statusFilter;
  });

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ maxHeight: '600px' }}>
      {/* Sticky Header - Current Design */}
      <div className={`sticky top-0 ${tokens.card.bg} ${tokens.border.light} border-b z-10`}>
        <div className="px-4 py-3">
          <h1 className={`text-2xl font-bold ${tokens.text.primary} mb-3`}>To Dos</h1>
          
          {/* Scope Filter */}
          <div className="flex gap-2 mb-3">
            <button
              className={`flex-1 px-4 py-2.5 rounded-landing text-sm font-semibold border min-h-[48px] flex items-center justify-center gap-2 transition-colors ${
                scope === 'my'
                  ? `${tokens.button.primary} border-transparent`
                  : `${tokens.card.bg} ${tokens.text.primary} ${tokens.border.light}`
              }`}
              onClick={() => onScopeChange('my')}
            >
              <span>My To Dos</span>
            </button>
            <button
              className={`flex-1 px-4 py-2.5 rounded-landing text-sm font-semibold border min-h-[48px] transition-colors ${
                scope === 'family'
                  ? `${tokens.button.primary} border-transparent`
                  : `${tokens.card.bg} ${tokens.text.primary} ${tokens.border.light}`
              }`}
              onClick={() => onScopeChange('family')}
            >
              Family To Dos
            </button>
          </div>

          {/* Status Filter */}
          <div className="flex gap-2 mb-3">
            {(['open', 'done', 'all'] as const).map((filter) => (
              <button
                key={filter}
                className={`flex-1 px-3 py-2 rounded-landing text-sm font-semibold border min-h-[48px] transition-colors ${
                  statusFilter === filter
                    ? `${tokens.button.secondary} ${tokens.border.accent} ${tokens.text.primary}`
                    : `${tokens.card.bg} ${tokens.border.light} ${tokens.text.primary}`
                }`}
                onClick={() => onStatusChange(filter)}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>

          {/* List Filter */}
          <div className="flex gap-2">
            <button
              className={`flex-1 px-3 py-2.5 rounded-landing text-sm font-semibold border ${tokens.border.light} ${tokens.card.bg} ${tokens.text.primary} flex items-center justify-between min-h-[48px]`}
              onClick={onListClick}
            >
              <span>{selectedListName}</span>
              <Icon name="arrow-right" size="sm" className={tokens.text.muted} />
            </button>
            <button
              className={`w-12 h-12 ${tokens.button.primary} rounded-landing flex items-center justify-center min-h-[48px] min-w-[48px]`}
              onClick={onCreateList}
            >
              <Icon name="plus" size="md" className="text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Todo List */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {filteredTodos.map(todo => (
          <Proposal1Card key={todo.todoId} todo={todo} isDarkMode={isDarkMode} />
        ))}
      </div>

      {/* FAB */}
      <button
        className={`fixed right-4 w-12 h-12 ${tokens.button.primary} shadow-lg z-30 rounded-full flex items-center justify-center min-h-[48px] min-w-[48px]`}
        style={{ bottom: '80px' }}
        onClick={onCreateTodo}
      >
        <Icon name="plus" size="md" className="text-white" />
      </button>
    </div>
  );
}

function FullUILayout2({ 
  todos, 
  isDarkMode, 
  scope, 
  statusFilter, 
  selectedListName,
  onScopeChange,
  onStatusChange,
  onListClick,
  onCreateList,
  onCreateTodo,
}: {
  todos: Todo[];
  isDarkMode: boolean;
  scope: 'my' | 'family';
  statusFilter: TodoStatus | 'all';
  selectedListName: string;
  onScopeChange: (scope: 'my' | 'family') => void;
  onStatusChange: (filter: TodoStatus | 'all') => void;
  onListClick: () => void;
  onCreateList: () => void;
  onCreateTodo: () => void;
}) {
  const tokens = getDesignTokens(isDarkMode);
  const filteredTodos = todos.filter(t => {
    if (statusFilter === 'all') return true;
    return t.status === statusFilter;
  });

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ maxHeight: '600px' }}>
      {/* Compact Header with Tabs */}
      <div className={`sticky top-0 ${tokens.card.bg} ${tokens.border.light} border-b z-10`}>
        <div className="px-4 py-2">
          <div className="flex items-center justify-between mb-2">
            <h1 className={`text-xl font-bold ${tokens.text.primary}`}>To Dos</h1>
            <button
              className={`w-10 h-10 ${tokens.button.primary} rounded-landing flex items-center justify-center min-h-[40px] min-w-[40px]`}
              onClick={onCreateTodo}
            >
              <Icon name="plus" size="sm" className="text-white" />
            </button>
          </div>
          
          {/* Compact Tabs */}
          <div className="flex gap-1 mb-2">
            <button
              className={`px-3 py-1.5 rounded-landing text-xs font-semibold min-h-[36px] transition-colors ${
                scope === 'my'
                  ? `${tokens.button.primary} text-white`
                  : `${tokens.card.bg} ${tokens.text.primary}`
              }`}
              onClick={() => onScopeChange('my')}
            >
              My
            </button>
            <button
              className={`px-3 py-1.5 rounded-landing text-xs font-semibold min-h-[36px] transition-colors ${
                scope === 'family'
                  ? `${tokens.button.primary} text-white`
                  : `${tokens.card.bg} ${tokens.text.primary}`
              }`}
              onClick={() => onScopeChange('family')}
            >
              Family
            </button>
            <div className="flex-1"></div>
            <button
              className={`px-3 py-1.5 rounded-landing text-xs ${tokens.text.muted} min-h-[36px]`}
              onClick={onListClick}
            >
              {selectedListName} <Icon name="chevron-down" size="sm" />
            </button>
          </div>

          {/* Status Pills */}
          <div className="flex gap-1">
            {(['open', 'done', 'all'] as const).map((filter) => (
              <button
                key={filter}
                className={`px-2 py-1 rounded-full text-xs font-medium min-h-[32px] transition-colors ${
                  statusFilter === filter
                    ? `${tokens.button.secondary} ${tokens.text.primary}`
                    : `${tokens.card.bg} ${tokens.text.muted}`
                }`}
                onClick={() => onStatusChange(filter)}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Todo List */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {filteredTodos.map(todo => (
          <Proposal2Card key={todo.todoId} todo={todo} isDarkMode={isDarkMode} />
        ))}
      </div>
    </div>
  );
}

function FullUILayout3({ 
  todos, 
  isDarkMode, 
  scope, 
  statusFilter, 
  selectedListName,
  onScopeChange,
  onStatusChange,
  onListClick,
  onCreateList,
  onCreateTodo,
}: {
  todos: Todo[];
  isDarkMode: boolean;
  scope: 'my' | 'family';
  statusFilter: TodoStatus | 'all';
  selectedListName: string;
  onScopeChange: (scope: 'my' | 'family') => void;
  onStatusChange: (filter: TodoStatus | 'all') => void;
  onListClick: () => void;
  onCreateList: () => void;
  onCreateTodo: () => void;
}) {
  const tokens = getDesignTokens(isDarkMode);
  const filteredTodos = todos.filter(t => {
    if (statusFilter === 'all') return true;
    return t.status === statusFilter;
  });

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ maxHeight: '600px' }}>
      {/* Minimal Header */}
      <div className={`sticky top-0 ${tokens.card.bg} ${tokens.border.light} border-b z-10`}>
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <h1 className={`text-2xl font-bold ${tokens.text.primary}`}>To Dos</h1>
            <div className="flex items-center gap-2">
              <button
                className={`p-2 rounded-landing ${tokens.button.secondary} min-h-[44px] min-w-[44px] flex items-center justify-center`}
                onClick={onListClick}
              >
                <Icon name="filter" size="sm" />
              </button>
              <button
                className={`p-2 rounded-landing ${tokens.button.primary} min-h-[44px] min-w-[44px] flex items-center justify-center`}
                onClick={onCreateTodo}
              >
                <Icon name="plus" size="sm" className="text-white" />
              </button>
            </div>
          </div>
          
          {/* Segmented Control for Scope */}
          <div className={`flex ${tokens.card.bg} ${tokens.border.light} border rounded-landing p-1 mb-2`}>
            <button
              className={`flex-1 px-3 py-2 rounded-landing text-sm font-semibold min-h-[44px] transition-colors ${
                scope === 'my'
                  ? `${tokens.button.primary} text-white`
                  : tokens.text.primary
              }`}
              onClick={() => onScopeChange('my')}
            >
              My
            </button>
            <button
              className={`flex-1 px-3 py-2 rounded-landing text-sm font-semibold min-h-[44px] transition-colors ${
                scope === 'family'
                  ? `${tokens.button.primary} text-white`
                  : tokens.text.primary
              }`}
              onClick={() => onScopeChange('family')}
            >
              Family
            </button>
          </div>

          {/* Status Segmented Control */}
          <div className={`flex ${tokens.card.bg} ${tokens.border.light} border rounded-landing p-1`}>
            {(['open', 'done', 'all'] as const).map((filter) => (
              <button
                key={filter}
                className={`flex-1 px-2 py-2 rounded-landing text-xs font-semibold min-h-[40px] transition-colors ${
                  statusFilter === filter
                    ? `${tokens.button.primary} text-white`
                    : tokens.text.primary
                }`}
                onClick={() => onStatusChange(filter)}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Todo List with Grouping */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        <Proposal3View todos={filteredTodos} isDarkMode={isDarkMode} />
      </div>
    </div>
  );
}

export default function MobileTodoDesignProposals() {
  const { isDarkMode, toggleTheme } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const [selectedProposal, setSelectedProposal] = useState<number | null>(null);
  const [demoScope, setDemoScope] = useState<'my' | 'family'>('family');
  const [demoStatusFilter, setDemoStatusFilter] = useState<TodoStatus | 'all'>('open');
  const [demoSelectedListName] = useState('All Lists');

  const proposals = [
    {
      id: 1,
      name: 'Current Design - Full Stacked Filters',
      description: 'Current implementation with all filters stacked vertically in sticky header. Clear separation, easy to understand.',
      fullUI: true,
      component: () => (
        <FullUILayout1
          todos={sampleTodos}
          isDarkMode={isDarkMode}
          scope={demoScope}
          statusFilter={demoStatusFilter}
          selectedListName={demoSelectedListName}
          onScopeChange={setDemoScope}
          onStatusChange={setDemoStatusFilter}
          onListClick={() => {}}
          onCreateList={() => {}}
          onCreateTodo={() => {}}
        />
      ),
      cardComponent: (todos: Todo[]) => (
        <div>
          {todos.map(todo => (
            <Proposal1Card key={todo.todoId} todo={todo} isDarkMode={isDarkMode} />
          ))}
        </div>
      ),
      pros: [
        'Clear visual hierarchy',
        'All filters visible at once',
        'Easy to understand',
        'Touch-friendly button sizes (48px+)',
        'Familiar stacked layout',
      ],
      cons: [
        'Takes significant header space',
        'Requires scrolling to see todos',
        'Less content visible on small screens',
      ],
      bestFor: 'Users who need all filters visible, less frequent users',
    },
    {
      id: 2,
      name: 'Compact Header with Tabs',
      description: 'Reduced header height with compact tabs and pills. More screen space for todos. Create button in header.',
      fullUI: true,
      component: () => (
        <FullUILayout2
          todos={sampleTodos}
          isDarkMode={isDarkMode}
          scope={demoScope}
          statusFilter={demoStatusFilter}
          selectedListName={demoSelectedListName}
          onScopeChange={setDemoScope}
          onStatusChange={setDemoStatusFilter}
          onListClick={() => {}}
          onCreateList={() => {}}
          onCreateTodo={() => {}}
        />
      ),
      cardComponent: (todos: Todo[]) => (
        <div>
          {todos.map(todo => (
            <Proposal2Card key={todo.todoId} todo={todo} isDarkMode={isDarkMode} />
          ))}
        </div>
      ),
      pros: [
        'Compact header saves space',
        'More todos visible at once',
        'Create button in header (no FAB)',
        'Modern tab/pill design',
        'Efficient use of screen space',
      ],
      cons: [
        'Smaller touch targets (36px)',
        'Less prominent filters',
        'May require horizontal scrolling for list name',
      ],
      bestFor: 'Users who want maximum content visibility, frequent users',
    },
    {
      id: 3,
      name: 'Segmented Controls + Grouped View',
      description: 'iOS-style segmented controls for filters. Automatic grouping by priority/date. Minimal header, maximum content.',
      fullUI: true,
      component: () => (
        <FullUILayout3
          todos={sampleTodos}
          isDarkMode={isDarkMode}
          scope={demoScope}
          statusFilter={demoStatusFilter}
          selectedListName={demoSelectedListName}
          onScopeChange={setDemoScope}
          onStatusChange={setDemoStatusFilter}
          onListClick={() => {}}
          onCreateList={() => {}}
          onCreateTodo={() => {}}
        />
      ),
      cardComponent: (todos: Todo[]) => (
        <Proposal3View todos={todos} isDarkMode={isDarkMode} />
      ),
      pros: [
        'iOS-style segmented controls',
        'Automatic grouping by priority/date',
        'Minimal header footprint',
        'Smart organization',
        'Critical items always visible',
      ],
      cons: [
        'Requires grouping logic',
        'May hide less urgent items',
        'More complex implementation',
        'Smaller filter buttons',
      ],
      bestFor: 'Users with many todos, priority-focused workflow, iOS users',
    },
    {
      id: 4,
      name: 'Quick Actions Bar (Full UI)',
      description: 'Uses current header design with todo cards that have bottom action bars. Each card shows edit, complete, and delete buttons.',
      fullUI: true,
      component: () => (
        <div className="h-full flex flex-col overflow-hidden" style={{ maxHeight: '600px' }}>
          {/* Use same header as Proposal 1 */}
          <div className={`sticky top-0 ${tokens.card.bg} ${tokens.border.light} border-b z-10`}>
            <div className="px-4 py-3">
              <h1 className={`text-2xl font-bold ${tokens.text.primary} mb-3`}>To Dos</h1>
              <div className="flex gap-2 mb-3">
                <button className={`flex-1 px-4 py-2.5 rounded-landing text-sm font-semibold border min-h-[48px] ${demoScope === 'family' ? tokens.button.primary : tokens.card.bg}`}>
                  Family To Dos
                </button>
                <button className={`flex-1 px-4 py-2.5 rounded-landing text-sm font-semibold border min-h-[48px] ${demoScope === 'my' ? tokens.button.primary : tokens.card.bg}`}>
                  My To Dos
                </button>
              </div>
              <div className="flex gap-2 mb-3">
                {(['open', 'done', 'all'] as const).map((filter) => (
                  <button key={filter} className={`flex-1 px-3 py-2 rounded-landing text-sm font-semibold border min-h-[48px] ${demoStatusFilter === filter ? tokens.button.secondary : tokens.card.bg}`}>
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button className={`flex-1 px-3 py-2.5 rounded-landing text-sm font-semibold border min-h-[48px] ${tokens.card.bg}`}>
                  {demoSelectedListName} <Icon name="arrow-right" size="sm" />
                </button>
                <button className={`w-12 h-12 ${tokens.button.primary} rounded-landing min-h-[48px] min-w-[48px] flex items-center justify-center`}>
                  <Icon name="plus" size="md" className="text-white" />
                </button>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-2">
            {sampleTodos.filter(t => demoStatusFilter === 'all' || t.status === demoStatusFilter).map(todo => (
              <Proposal4Card key={todo.todoId} todo={todo} isDarkMode={isDarkMode} />
            ))}
          </div>
          <button className={`fixed right-4 w-12 h-12 ${tokens.button.primary} shadow-lg z-30 rounded-full flex items-center justify-center min-h-[48px] min-w-[48px]`} style={{ bottom: '80px' }}>
            <Icon name="plus" size="md" className="text-white" />
          </button>
        </div>
      ),
      cardComponent: (todos: Todo[]) => (
        <div>
          {todos.map(todo => (
            <Proposal4Card key={todo.todoId} todo={todo} isDarkMode={isDarkMode} />
          ))}
        </div>
      ),
      pros: [
        'Clear action affordances',
        'Balanced visibility and density',
        'Touch-optimized buttons',
        'No hidden interactions',
      ],
      cons: [
        'Takes more space per item',
        'More scrolling required',
        'Less minimalist',
      ],
      bestFor: 'General use, balanced approach',
    },
    {
      id: 5,
      name: 'Minimalist with Long Press (Full UI)',
      description: 'Uses compact header with minimal todo cards. Long press reveals context menu. Swipe to complete. Maximum content density.',
      fullUI: true,
      component: () => (
        <div className="h-full flex flex-col overflow-hidden" style={{ maxHeight: '600px' }}>
          {/* Use compact header like Proposal 2 */}
          <div className={`sticky top-0 ${tokens.card.bg} ${tokens.border.light} border-b z-10`}>
            <div className="px-4 py-2">
              <div className="flex items-center justify-between mb-2">
                <h1 className={`text-xl font-bold ${tokens.text.primary}`}>To Dos</h1>
                <button className={`w-10 h-10 ${tokens.button.primary} rounded-landing flex items-center justify-center min-h-[40px] min-w-[40px]`}>
                  <Icon name="plus" size="sm" className="text-white" />
                </button>
              </div>
              <div className="flex gap-1 mb-2">
                <button className={`px-3 py-1.5 rounded-landing text-xs font-semibold min-h-[36px] ${demoScope === 'family' ? tokens.button.primary : tokens.card.bg}`}>
                  Family
                </button>
                <button className={`px-3 py-1.5 rounded-landing text-xs font-semibold min-h-[36px] ${demoScope === 'my' ? tokens.button.primary : tokens.card.bg}`}>
                  My
                </button>
                <div className="flex-1"></div>
                <button className={`px-3 py-1.5 rounded-landing text-xs min-h-[36px] ${tokens.text.muted}`}>
                  {demoSelectedListName} <Icon name="chevron-down" size="sm" />
                </button>
              </div>
              <div className="flex gap-1">
                {(['open', 'done', 'all'] as const).map((filter) => (
                  <button key={filter} className={`px-2 py-1 rounded-full text-xs font-medium min-h-[32px] ${demoStatusFilter === filter ? tokens.button.secondary : tokens.card.bg}`}>
                    {filter}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-2">
            {sampleTodos.filter(t => demoStatusFilter === 'all' || t.status === demoStatusFilter).map(todo => (
              <Proposal5Card key={todo.todoId} todo={todo} isDarkMode={isDarkMode} />
            ))}
          </div>
        </div>
      ),
      cardComponent: (todos: Todo[]) => (
        <div>
          {todos.map(todo => (
            <Proposal5Card key={todo.todoId} todo={todo} isDarkMode={isDarkMode} />
          ))}
        </div>
      ),
      pros: [
        'Very clean appearance',
        'Maximum information density',
        'Familiar long-press pattern',
        'Minimal UI clutter',
      ],
      cons: [
        'Requires discovery',
        'Long press may be slow',
        'Less immediate actions',
      ],
      bestFor: 'Users who prefer clean interfaces, experienced mobile users',
    },
  ];

  return (
    <div className={`min-h-screen ${tokens.bg.gradient} transition-colors duration-200`}>
      <NavBar className={`${tokens.card.bg} ${tokens.border.light} border-b shadow-sm`} containerClassName="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between w-full items-center">
          <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Mobile Todo Design Proposals</h1>
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={toggleTheme}
              className={`${tokens.button.secondary} px-4 h-12 rounded-sm text-sm font-medium min-w-[100px] flex items-center justify-center gap-2 transition-all active:scale-95`}
            >
              <Icon 
                name={isDarkMode ? 'sun' : 'moon'} 
                size="md" 
                className={tokens.text.primary}
              />
              <span>{isDarkMode ? 'Light' : 'Dark'}</span>
            </button>
          </div>
        </div>
      </NavBar>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h2 className={`text-3xl font-bold ${tokens.text.primary} mb-4`}>
            Mobile Todo View & Management Proposals
          </h2>
          <p className={`text-lg ${tokens.text.secondary} mb-2`}>
            Five different approaches for the complete mobile todo interface including filters, header, and todo cards
          </p>
          <p className={`text-sm ${tokens.text.muted} mb-4`}>
            Based on research: swipe gestures, touch targets (44-48px), card-based layouts, and gesture-driven interfaces. 
            Each proposal shows the full UI including scope filters (My/Family), status filters (Open/Done/All), list selection, and todo cards.
          </p>
          <div className={`${tokens.card.bg} ${tokens.border.light} border rounded-landing p-4 mb-6`}>
            <h3 className={`text-sm font-semibold ${tokens.text.primary} mb-2`}>Key Design Principles Applied:</h3>
            <ul className={`text-xs ${tokens.text.muted} space-y-1 list-disc list-inside`}>
              <li>Touch targets minimum 44x44px (WCAG 2.1 AA standard)</li>
              <li>Swipe gestures for quick actions (complete, delete)</li>
              <li>Card-based layouts with clear visual hierarchy</li>
              <li>One screen, one goal principle</li>
              <li>Immediate visual feedback on interactions</li>
              <li>Pull-to-refresh for data updates</li>
            </ul>
          </div>
        </div>

        {/* Proposals Grid */}
        <div className="space-y-8 mb-8">
          {proposals.map((proposal) => (
            <div
              key={proposal.id}
              className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 ${tokens.card.shadow} ${
                selectedProposal === proposal.id ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <div className="mb-4">
                <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-2`}>
                  Proposal {proposal.id}: {proposal.name}
                </h3>
                <p className={`text-sm ${tokens.text.secondary} mb-4`}>
                  {proposal.description}
                </p>
              </div>

              {/* Example Implementation */}
              <div className="mb-4">
                <h4 className={`text-sm font-semibold ${tokens.text.secondary} mb-3`}>
                  {proposal.fullUI ? 'Full UI Implementation:' : 'Example Implementation:'}
                </h4>
                <div className={`${tokens.bg.secondary} rounded-landing p-4 ${proposal.fullUI ? 'border-2 border-dashed' : ''}`}>
                  {proposal.fullUI ? (
                    proposal.component()
                  ) : (
                    proposal.component()
                  )}
                </div>
                {proposal.fullUI && proposal.cardComponent && (
                  <div className="mt-4">
                    <h5 className={`text-xs font-semibold ${tokens.text.secondary} mb-2`}>
                      Todo Card Detail:
                    </h5>
                    <div className={`${tokens.bg.secondary} rounded-landing p-4`}>
                      {proposal.cardComponent(sampleTodos.slice(0, 2))}
                    </div>
                  </div>
                )}
              </div>

              {/* Pros/Cons */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <h5 className={`text-xs font-semibold ${tokens.text.primary} mb-2`}>Pros:</h5>
                  <ul className={`text-xs ${tokens.text.muted} space-y-1 list-disc list-inside`}>
                    {proposal.pros.map((pro, idx) => (
                      <li key={idx}>{pro}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h5 className={`text-xs font-semibold ${tokens.text.primary} mb-2`}>Cons:</h5>
                  <ul className={`text-xs ${tokens.text.muted} space-y-1 list-disc list-inside`}>
                    {proposal.cons.map((con, idx) => (
                      <li key={idx}>{con}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-4">
                <p className={`text-xs ${tokens.text.muted}`}>
                  <strong>Best for:</strong> {proposal.bestFor}
                </p>
              </div>

              <button
                onClick={() => setSelectedProposal(proposal.id === selectedProposal ? null : proposal.id)}
                className={`mt-4 w-full px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                  selectedProposal === proposal.id
                    ? `${tokens.button.primary} text-white`
                    : `${tokens.button.secondary}`
                }`}
              >
                {selectedProposal === proposal.id ? 'Selected' : 'Select This Proposal'}
              </button>
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 ${tokens.card.shadow} mb-8`}>
          <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-4`}>Comparison</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`${tokens.border.medium} border-b`}>
                  <th className={`text-left py-2 px-4 ${tokens.text.primary} font-semibold`}>Proposal</th>
                  <th className={`text-left py-2 px-4 ${tokens.text.primary} font-semibold`}>Header Space</th>
                  <th className={`text-left py-2 px-4 ${tokens.text.primary} font-semibold`}>Touch Targets</th>
                  <th className={`text-left py-2 px-4 ${tokens.text.primary} font-semibold`}>Content Visible</th>
                  <th className={`text-left py-2 px-4 ${tokens.text.primary} font-semibold`}>Best For</th>
                </tr>
              </thead>
              <tbody>
                {proposals.map((proposal) => (
                  <tr key={proposal.id} className={`${tokens.border.light} border-b`}>
                    <td className={`py-2 px-4 ${tokens.text.secondary} font-medium`}>{proposal.name}</td>
                    <td className={`py-2 px-4 ${tokens.text.muted}`}>
                      {proposal.id === 1 && 'Large (~200px)'}
                      {proposal.id === 2 && '✅ Compact (~120px)'}
                      {proposal.id === 3 && '✅ Compact (~140px)'}
                      {proposal.id === 4 && 'Large (~200px)'}
                      {proposal.id === 5 && '✅ Compact (~120px)'}
                    </td>
                    <td className={`py-2 px-4 ${tokens.text.muted}`}>
                      {proposal.id === 1 && '✅ 48px+ (header)'}
                      {proposal.id === 2 && '36-40px (header)'}
                      {proposal.id === 3 && '40px (header)'}
                      {proposal.id === 4 && '✅ 48px+ (header)'}
                      {proposal.id === 5 && '36-40px (header)'}
                    </td>
                    <td className={`py-2 px-4 ${tokens.text.muted}`}>
                      {proposal.id === 1 && 'Less (large header)'}
                      {proposal.id === 2 && '✅ More (compact)'}
                      {proposal.id === 3 && '✅ More (compact + grouped)'}
                      {proposal.id === 4 && 'Less (large header)'}
                      {proposal.id === 5 && '✅ Most (compact + minimal cards)'}
                    </td>
                    <td className={`py-2 px-4 ${tokens.text.muted}`}>
                      {proposal.id === 1 && 'Less frequent users'}
                      {proposal.id === 2 && 'Frequent users'}
                      {proposal.id === 3 && 'Many todos, priority-focused'}
                      {proposal.id === 4 && 'General use'}
                      {proposal.id === 5 && 'Power users, clean UI'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Implementation Notes */}
        <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 ${tokens.card.shadow}`}>
          <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-4`}>Implementation Notes</h3>
          <div className={`text-sm ${tokens.text.secondary} space-y-3`}>
            <div>
              <h4 className={`font-semibold ${tokens.text.primary} mb-2`}>Current Implementation:</h4>
              <p className={tokens.text.muted}>
                The app currently uses swipe-to-complete (swipe right for open → done, swipe left for done → open).
                This is similar to Proposal 1 but only supports status toggle.
              </p>
            </div>
            <div>
              <h4 className={`font-semibold ${tokens.text.primary} mb-2`}>Recommended Approach:</h4>
              <p className={tokens.text.muted}>
                Consider a hybrid approach: Use Proposal 1 (Enhanced Swipe Actions) for power users, 
                with an option to enable Proposal 2 (Inline Actions) for users who prefer visible buttons.
                Proposal 3 (Grouped View) could be a toggle option for better organization.
              </p>
            </div>
            <div>
              <h4 className={`font-semibold ${tokens.text.primary} mb-2`}>Accessibility Considerations:</h4>
              <ul className={`${tokens.text.muted} space-y-1 list-disc list-inside`}>
                <li>All interactive elements must meet 44x44px minimum touch target size</li>
                <li>Swipe gestures should have alternative tap-based actions</li>
                <li>Long press actions should have alternative access methods</li>
                <li>Visual feedback must be clear and immediate</li>
                <li>Consider haptic feedback for swipe actions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
